import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  FileText,
  UserPlus,
  CalendarPlus,
  Receipt,
  Sparkles,
  X,
} from "lucide-react";
import AskAiSheet from "./AskAiSheet";
import { useAuth } from "@/contexts/AuthContext";

/**
 * One floating action button for the whole app:
 * - Logged in: a quick-create speed dial (prescription, patient, …)
 * - Logged out: a support / live-chat assistant
 */
export default function GlobalFab() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const handler = (e: Event) => setUnread((e as CustomEvent<number>).detail ?? 0);
    window.addEventListener("chat:unread", handler);
    return () => window.removeEventListener("chat:unread", handler);
  }, []);

  if (loading) return null;
  if (!session) return <AskAiSheet variant="support" />;


  const actions = [
    { label: "New prescription", icon: FileText, run: () => navigate("/prescriptions/new") },
    { label: "New patient", icon: UserPlus, run: () => navigate("/patients/new") },
    { label: "New appointment", icon: CalendarPlus, run: () => navigate("/appointments?new=1") },
    { label: "New invoice", icon: Receipt, run: () => navigate("/billing?new=1") },
    { label: "Ask AI", icon: Sparkles, run: () => setAiOpen(true) },
  ];

  return (
    <>
      {open && (
        <button
          aria-label="Close quick create"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-20 bg-foreground/10 backdrop-blur-[2px]"
        />
      )}

      <div className="fixed bottom-6 right-6 z-30 flex flex-col items-end gap-2">
        {open &&
          actions.map((a, i) => (
            <button
              key={a.label}
              onClick={() => {
                setOpen(false);
                a.run();
              }}
              style={{ animationDelay: `${i * 35}ms` }}
              className="animate-fade-in flex items-center gap-2 rounded-full bg-card pl-4 pr-2 py-2 text-sm font-medium shadow-lg hover:bg-accent transition"
            >
              <span className="whitespace-nowrap">{a.label}</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                <a.icon className="h-4 w-4" />
              </span>
            </button>
          ))}

        <button
          onClick={() => setOpen((o) => !o)}
          title="Quick create"
          aria-expanded={open}
          className="group relative h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl active:scale-95"
        >
          {!open && (
            <span className="pointer-events-none absolute inset-0 rounded-full bg-primary/40 animate-ping" />
          )}
          <span
            className={`relative transition-transform duration-300 ${open ? "rotate-90" : "group-hover:rotate-90"}`}
          >
            {open ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          </span>
          {unread > 0 && !open && (
            <span
              onClick={(e) => { e.stopPropagation(); setAiOpen(true); }}
              className="absolute -top-1 -right-1 flex h-5 min-w-5 animate-bounce items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground shadow-md"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>

      </div>

      <AskAiSheet showTrigger={false} open={aiOpen} onOpenChange={setAiOpen} />
    </>
  );
}
